import clientApi from "./client";
import { MeetingField, Suggestion, type MapCoverageItem } from "@/types/sections";

/**
 * API Response type for meetings
 */
export interface MeetingApiResponse {
  id: number;
  date_time: string;
  end_date_time?: string;
  name: string;
  logo: string;
  sales_stage: string;
  attendees: string[];
  account_id: number;
}

/**
 * Historical Context Activity - Meeting Type
 */
export interface HistoricalContextMeetingActivity {
  id: number;
  type: "meeting";
  timestamp: string;
  details: {
    title?: string;
    attendees: Array<{
      name: string;
      img_url: string;
      linkedin_url: string;
    }>;
    type: string;
    highlights: string[];
    next_steps: string[];
    suggestions: any[];
    insights_used: number;
    has_call_plan: boolean;
    map_label?: string | null;
    map_coverage?: Array<{
      dimension: string;
      label: string;
      status: string;
      score: number | null;
      description?: string | null;
    }> | null;
    momentum_label?: string | null;
    momentum_direction?: string | null;
  };
}

/**
 * Historical Context Activity - Engagement Type
 */
export interface HistoricalContextEngagementActivity {
  id: number;
  type: "engagement";
  timestamp: string;
  details: {
    content_title: string;
    case_study: string;
    engagement_metrics: {
      duration: string;
      read_time: number;
    };
  };
}

/**
 * Historical Context API Response
 */
export interface DealMomentumBlocker {
  key: string;
  label: string;
  reason: string;
}

export interface DealMomentumResponse {
  account_id: number;
  meeting_id: number;
  momentum_label: string | null;
  momentum_direction: string | null;
  has_deal_momentum_data: boolean;
  call_count?: number;
  blockers: DealMomentumBlocker[];
  note: string | null;
}

export interface HistoricalContextApiResponse {
  activities: Array<HistoricalContextMeetingActivity | HistoricalContextEngagementActivity>;
  has_more?: boolean;
  page?: number;
  page_size?: number;
  total_count?: number;
  current_page?: number;
  total_pages?: number;
  limit?: number;
}

/**
 * MAP Progress API Response
 */
export interface CallPlanProgressApiResponse {
  total_percentage?: number;
  breakdown?: {
    big_decision?: number | null;
    little_decision?: number | null;
    detours?: number | null;
    preconditions?: number | null;
  };
}

/**
 * Legacy Historical Context API Response (for backward compatibility)
 */
export interface HistoricalContextApiResponseLegacy {
  id: number;
  date_time: string;
  type: string;
  attendees: Array<{
    name: string;
    img_url: string;
    linkedin_url: string;
  }>;
  highlights: string[];
  next_steps: string[];
  suggestions: any[];
}

/**
 * Participant in MAP
 */
export interface CallPlanParticipant {
  id?: number;
  name?: string;
  image_url?: string;
}

/**
 * MAP discussion point/item
 */
export interface CallPlanItem {
  id?: number;
  title?: string;
  description?: string;
  challenge?: string;
  response?: string;
  is_liked?: boolean;
  is_disliked?: boolean;
  added_to_meeting?: boolean;
  theme?: string;
}

/**
 * Finalize Review Call API Response
 */
export interface FinalizeReviewCallApiResponse {
  basic_information?: {
    company?: string;
    opportunity?: string;
    sales_rep_name?: string;
    meeting_date?: string;
    client_participants?: CallPlanParticipant[];
    internal_participants?: CallPlanParticipant[];
  };
  preconditions?: {
    precondition_the_meeting?: string;
    opening_statement?: string;
    precondition_the_next_meeting?: string;
  };
  big_decision?: CallPlanItem[];
  little_decision?: CallPlanItem[];
  detours?: CallPlanItem[];
}

/**
 * API Response type for meetings grouped by week
 */
export interface MeetingsByWeekApiResponse {
  this_week_meetings: MeetingApiResponse[];
  upcomming_meetings: MeetingApiResponse[];
}

/**
 * Service to fetch meetings data
 */
export class MeetingsService {
  /**
   * Fetch meetings for a specific user email
   * @param email - The user's email address
   * @returns Promise with meetings grouped by week
   */
  static async getMeetings(email: string): Promise<MeetingsByWeekApiResponse> {
    // URL encode the email to handle special characters
    const encodedEmail = encodeURIComponent(email);
    const isUat = process.env.NEXT_PUBLIC_IS_UAT === "true";
    const response = await clientApi
      .get(`api/meetings/${encodedEmail}?is_uat=${isUat}`)
      .json<MeetingsByWeekApiResponse>();
    
    return response;
  }

  /**
   * @param email - The user's email address
   * @param accountId - The account ID from config API
   * @returns Promise with historical context response containing activities
   */
  static async getHistoricalContext(
    accountId: number,
    meetingId: number,
    page: number = 1,
    pageSize: number = 5
  ): Promise<HistoricalContextApiResponse> {
    // URL encode the email to handle special characters

    const response = await clientApi
      .get(
        `api/meetings/historical-context/${accountId}`,
        {
          searchParams: {
            meeting_id: meetingId,
            page: page.toString(),
            page_size: pageSize.toString(),
          },
        }
      )
      .json<HistoricalContextApiResponse>();

    const resolvedPage = response.page ?? response.current_page ?? page;
    const resolvedPageSize = response.page_size ?? response.limit ?? pageSize;
    const totalCount = response.total_count;
    const hasMore =
      typeof response.has_more === "boolean"
        ? response.has_more
        : typeof totalCount === "number" &&
            typeof resolvedPage === "number" &&
            typeof resolvedPageSize === "number"
          ? resolvedPage * resolvedPageSize < totalCount
          : typeof response.current_page === "number" &&
              typeof response.total_pages === "number" &&
              response.total_pages > 0
            ? response.current_page < response.total_pages
            : false;

    return {
      ...response,
      activities: response.activities ?? [],
      page: resolvedPage,
      page_size: resolvedPageSize,
      total_count: totalCount,
      has_more: hasMore,
      current_page: resolvedPage,
      total_pages:
        response.total_pages ??
        (typeof totalCount === "number" && resolvedPageSize > 0
          ? Math.max(1, Math.ceil(totalCount / resolvedPageSize))
          : 1),
      limit: resolvedPageSize,
    };
  }

  static async getDealMomentum(meetingId: number): Promise<DealMomentumResponse> {
    return clientApi
      .get(`api/meetings/deal_momentum/${meetingId}`)
      .json<DealMomentumResponse>();
  }

  /**
   * Transform historical context API response to MeetingField format
   * @param historicalContext - Historical context response from API
   * @returns Array of MeetingField objects sorted by timestamp (newest first)
   */
  static transformHistoricalContextToFields(
    historicalContext: HistoricalContextApiResponse
  ): MeetingField[] {
    const activities = historicalContext?.activities || [];
    if (!Array.isArray(activities)) return [];
    
    // Sort by timestamp descending (newest first)
    const sortedActivities = [...activities]
      .filter((activity) => activity != null && activity.timestamp)
      .sort((a, b) => {
        try {
      const dateA = new Date(a.timestamp).getTime();
      const dateB = new Date(b.timestamp).getTime();
      return dateB - dateA; // Descending order
        } catch {
          return 0;
        }
    });

    // Helper to transform raw suggestions from historical context into Suggestion[]
    const transformHistoricalSuggestions = (
      suggestions?: Array<{
        id?: number | string;
        title?: string;
        description?: string;
        challenge?: string | null;
        response?: string | null;
        is_liked?: boolean;
        is_disliked?: boolean;
        added_to_meeting?: boolean;
      }>
    ): Suggestion[] => {
      if (!Array.isArray(suggestions)) return [];

      return suggestions
        .filter((s) => s != null)
        .map((s, idx) => ({
          title: s.title || "",
          challenge: s.challenge ?? undefined,
          response: s.response ?? undefined,
          actions: [
            {
              icon: "plus",
              type: "button",
              label: "Add to MAP",
              action: "add_to_meeting",
              variant: "primary",
              action_id: `action_add_historical_${idx}`,
            },
            {
              icon: "thumbs-up",
              type: "icon_button",
              action: "like_suggestion",
              variant: "ghost",
              action_id: `action_like_historical_${idx}`,
            },
            {
              icon: "thumbs-down",
              type: "icon_button",
              action: "dislike_suggestion",
              variant: "ghost",
              action_id: `action_dislike_historical_${idx}`,
            },
          ],
          content: {
            text: s.description || "",
            challenge: s.challenge ?? undefined,
            response: s.response ?? undefined,
            has_icon: true,
            icon_type: "lightbulb",
          },
          metadata: {
            is_liked: s.is_liked ?? false,
            is_disliked: s.is_disliked ?? false,
            added_to_meeting: s.added_to_meeting ?? false,
          },
          id: String(s.id ?? idx),
        }));
    };

    return sortedActivities.map((activity) => {
      // Convert timestamp to ISO format
      let dateTime = "";
      try {
        dateTime = new Date(activity.timestamp).toISOString();
      } catch {
        dateTime = activity.timestamp || "";
      }
      
      if (activity.type === "meeting") {
        const meetingActivity = activity as HistoricalContextMeetingActivity;
        const details = meetingActivity.details || {};
        // Ensure ID is a valid number
        const meetingId = typeof meetingActivity.id === "number" 
          ? meetingActivity.id 
          : typeof meetingActivity.id === "string" 
          ? parseInt(meetingActivity.id, 10) 
          : Number(meetingActivity.id);
        const validId = !isNaN(meetingId) && meetingId > 0 ? meetingId : "";
        return {
          name: details.title || "",
          type: "meeting" as const,
          datetime: dateTime,
          field_id: validId ? `${validId}` : undefined,
          attendees: (Array.isArray(details.attendees) ? details.attendees : [])
            .filter((attendee) => attendee != null)
            .map((attendee) => ({
              avatar_url: attendee.img_url || "",
              name: attendee.name || "",
          })),
          highlights: Array.isArray(details.highlights) ? details.highlights : [],
          next_step_prompts: Array.isArray(details.next_steps) ? details.next_steps : [],
          suggestions: transformHistoricalSuggestions(details.suggestions),
          map_label: details.map_label ?? null,
          map_coverage: Array.isArray(details.map_coverage)
            ? (details.map_coverage as MapCoverageItem[])
            : undefined,
          momentum_label: details.momentum_label ?? null,
          momentum_direction: details.momentum_direction ?? null,
          call_plan: details.has_call_plan ? {
            date: dateTime,
            insights_used: details.insights_used || 0,
            download_enabled: true,
          } : undefined,
          component_type: "meeting" as const,
        };
      } else {
        const engagementActivity = activity as HistoricalContextEngagementActivity;
        const details = engagementActivity.details || {};
        const metrics = details.engagement_metrics || {};
        return {
          name: details.content_title || "",
          type: "engagement" as const,
          datetime: dateTime,
          field_id: `engagement_${engagementActivity.id || ""}`,
          details: {
            engagement: {
              duration: metrics.duration || "",
            },
            client_viewed_case_study: details.case_study || "",
          },
          component_type: "engagement" as const,
        };
      }
    });
  }

  /**
   * Fetch finalize review call data for step 3
   * @param meetingId - The meeting ID
   * @returns Promise with finalize review call response
   */
  static async getFinalizeReviewCall(meetingId: number): Promise<FinalizeReviewCallApiResponse> {
    const response = await clientApi
      .get(`api/meetings/finalize_review_call`, {
        searchParams: {
          meeting_id: meetingId,
        },
      })
      .json<FinalizeReviewCallApiResponse>();
    
    return response;
  }


  static async getCallPlanProgress(meetingId: number): Promise<CallPlanProgressApiResponse> {
    const response = await clientApi
      .get(`api/meetings/call_plan_progress`, {
        searchParams: {
          meeting_id: meetingId,
        },
      })
      .json<CallPlanProgressApiResponse>();
    
    return response;
  }

  /**
   * Update preconditions for a meeting
   * @param meetingId - The meeting ID
   * @param preconditions - The preconditions data
   * @returns Promise with updated preconditions response
   */
  static async updatePreconditions(
    meetingId: number,
    preconditions: {
      precondition_the_meeting: string;
      opening_statement: string;
      precondition_the_next_meeting: string;
    }
  ): Promise<{
    precondition_the_meeting: string;
    opening_statement: string;
    precondition_the_next_meeting: string;
  }> {
    const response = await clientApi
      .put(`api/meetings/preconditions/${meetingId}`, {
        json: preconditions,
      })
      .json<{
        precondition_the_meeting: string;
        opening_statement: string;
        precondition_the_next_meeting: string;
      }>();
    
    return response;
  }

  /**
   * Create a custom suggestion for the MAP
   * @param meetingId - The meeting ID (number or string that can be converted to number)
   * @param key - Suggestion key (e.g., "Big_decision")
   * @param description - Custom suggestion text
   */
  static async createCustomSuggestion(
    meetingId: number | string,
    key: string,
    description: string
  ): Promise<{
    id: number;
    title: string;
    description: string;
    is_liked: boolean;
    is_disliked: boolean;
    added_to_meeting: boolean;
    theme?: string;
  }> {
    const numericMeetingId = typeof meetingId === "number"
      ? meetingId
      : typeof meetingId === "string"
      ? parseInt(meetingId, 10)
      : Number(meetingId);

    if (isNaN(numericMeetingId) || numericMeetingId <= 0) {
      throw new Error(`Invalid meeting ID for custom suggestion: ${meetingId}. Expected a positive number.`);
    }

    const response = await clientApi
      .post(`api/meetings/custom-suggestion/${numericMeetingId}`, {
        json: {
          key,
          description,
        },
      })
      .json<{
        id: number;
        title: string;
        description: string;
        is_liked: boolean;
        is_disliked: boolean;
        added_to_meeting: boolean;
        theme?: string;
      }>();
    
    return response;
  }

  static async createCustomDetour(
    meetingId: number | string,
    challenge: string,
    response: string
  ): Promise<{
    id: number;
    title: string;
    description: string;
    challenge: string;
    response: string;
    is_liked: boolean;
    is_disliked: boolean;
    added_to_meeting: boolean;
    theme?: string;
  }> {
    const numericMeetingId =
      typeof meetingId === "number"
        ? meetingId
        : typeof meetingId === "string"
          ? parseInt(meetingId, 10)
          : Number(meetingId);

    if (isNaN(numericMeetingId) || numericMeetingId <= 0) {
      throw new Error(
        `Invalid meeting ID for custom detour: ${meetingId}. Expected a positive number.`
      );
    }

    const res = await clientApi
      .post(`api/meetings/custom-suggestion/${numericMeetingId}`, {
        json: {
          key: "detours",
          description: "",
          challenge: challenge || "",
          response: response || "",
        },
      })
      .json<{
        id: number;
        title: string;
        description: string;
        challenge?: string;
        response?: string;
        is_liked: boolean;
        is_disliked: boolean;
        added_to_meeting: boolean;
        theme?: string;
      }>();

    return {
      ...res,
      challenge: res.challenge ?? challenge,
      response: res.response ?? response,
    };
  }

  /**
   * Fetch call details for a meeting
   * @param meetingId - The meeting ID
   * @returns Promise with call details response containing highlights, next_steps, action_items, and insights
   */
  static async getCallDetails(meetingId: number): Promise<{
    highlights: string[];
    next_steps: string[];
    action_items: string[];
    insights: string[];
  }> {
    const response = await clientApi
      .get(`api/meetings/call_details/${meetingId}`)
      .json<{
        highlights: string[];
        next_steps: string[];
        action_items: string[];
        insights: string[];
      }>();
    
    return response;
  }

  static async downloadCallPlan(meetingId: number | string): Promise<{
    id: number;
    call_plan_download_link: string;
  }> {
    // Ensure meetingId is a valid number
    const numericMeetingId = typeof meetingId === "number" 
      ? meetingId 
      : typeof meetingId === "string" 
      ? parseInt(meetingId, 10) 
      : Number(meetingId);
    
    if (isNaN(numericMeetingId) || numericMeetingId <= 0) {
      throw new Error(`Invalid meeting ID: ${meetingId}. Expected a positive number.`);
    }
    
    const response = await clientApi
      .get(`api/meetings/download/${numericMeetingId}`)
      .json<{
        id: number;
        call_plan_download_link: string;
      }>();
    
    return response;
  }
}

